import { useState, useEffect } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import { SettingCombobox } from '../../components/admin/SettingCombobox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  getAccessCodes,
  createAccessCode,
  revokeAccessCode,
  getAccessCodeSuggestions,
  getAccessCodeAuditLog,
  getAccessCodeSettings,
  type AccessCode,
  type AuditLogEntry,
  type ClinicalSettingOption,
} from '../../lib/admin-api';
import {
  Loader2,
  AlertCircle,
  KeyRound,
  Plus,
  ShieldOff,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Shield,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

export default function AccessCodesView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [newCode, setNewCode] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newSetting, setNewSetting] = useState<string>('');
  const [settingOptions, setSettingOptions] = useState<ClinicalSettingOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Audit log toggle
  const [showAudit, setShowAudit] = useState(false);

  // Revoke confirmation
  const [revoking, setRevoking] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [codesRes, auditRes] = await Promise.all([
        getAccessCodes(),
        getAccessCodeAuditLog(50),
      ]);
      setCodes(codesRes.codes);
      setAuditLog(auditRes.entries);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      loadSuggestions();
      loadSettingOptions();
    }
  }, [isAuthenticated]);

  const loadSettingOptions = async () => {
    try {
      const { settings } = await getAccessCodeSettings();
      setSettingOptions(settings);
    } catch {
      // Silently fail — the create form will block submission until settings load.
    }
  };

  const loadSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const { suggestions: words } = await getAccessCodeSuggestions();
      setSuggestions(words);
    } catch {
      // Silently fail
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCode.trim()) return;
    if (!newSetting) {
      setCreateError('Please select a clinical setting for this code.');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await createAccessCode(
        newCode.trim().toLowerCase(),
        newSetting,
        undefined,
        newNotes || undefined,
      );
      setNewCode('');
      setNewNotes('');
      setNewSetting('');
      await fetchData();
      loadSuggestions();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: number) => {
    setRevoking(id);
    try {
      await revokeAccessCode(id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke code');
    } finally {
      setRevoking(null);
    }
  };

  if (authLoading) {
    return (
      <AdminLayout title="Access Codes">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  const activeCodes = codes.filter((c) => c.status === 'active');
  const revokedCodes = codes.filter((c) => c.status === 'revoked');

  return (
    <AdminLayout title="Access Codes">
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Codes</CardTitle>
            <KeyRound className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{codes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Shield className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeCodes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revoked</CardTitle>
            <ShieldOff className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{revokedCodes.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Create Code */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Access Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Word suggestions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Pick a word or type your own:</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadSuggestions}
                disabled={suggestionsLoading || creating}
                className="text-xs"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${suggestionsLoading ? 'animate-spin' : ''}`} />
                New words
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestionsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              ) : (
                suggestions.map((word) => (
                  <button
                    key={word}
                    onClick={() => setNewCode(word)}
                    disabled={creating}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-mono font-medium border transition-colors',
                      newCode === word
                        ? 'bg-blue-100 border-blue-400 text-blue-800'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                    )}
                  >
                    {word}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Manual input + notes + create */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Or type a custom word..."
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toLowerCase())}
              maxLength={5}
              className="flex-1 font-mono text-lg"
              disabled={creating}
            />
            <Input
              placeholder="Notes (optional)"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              className="flex-1"
              disabled={creating}
            />
          </div>

          {/* Setting selector (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clinical setting <span className="text-red-600">*</span>
            </label>
            <SettingCombobox
              value={newSetting}
              options={settingOptions.map(o => ({
                id: o.id,
                displayName: o.displayName,
                group: o.group,
              }))}
              onChange={setNewSetting}
              disabled={creating || settingOptions.length === 0}
              placeholder="Select a setting…"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={!newCode.trim() || !newSetting || creating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </>
              )}
            </Button>
          </div>
          {createError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}
          <p className="text-xs text-muted-foreground">
            Codes are 4-5 lowercase letters. Already-used words are excluded from suggestions.
          </p>
        </CardContent>
      </Card>

      {/* Codes Table */}
      <Card className="mb-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">All Codes</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : codes.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No access codes created yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Setting</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Created</TableHead>
                  <TableHead className="hidden sm:table-cell">Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono text-lg font-bold">
                      {code.code}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {settingOptions.find(o => o.id === code.clinicalSetting)?.displayName ?? code.clinicalSetting}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {code.status === 'active' ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          Active
                        </Badge>
                      ) : code.status === 'revoked' ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : (
                        <Badge variant="secondary">Expired</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                      {format(new Date(code.createdAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground max-w-[200px] truncate">
                      {code.notes || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {code.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevoke(code.id)}
                          disabled={revoking === code.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {revoking === code.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ShieldOff className="h-3 w-3 mr-1" />
                          )}
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowAudit(!showAudit)}
            className="flex items-center justify-between w-full text-left"
          >
            <CardTitle className="text-lg">Verification Audit Log</CardTitle>
            {showAudit ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </CardHeader>
        {showAudit && (
          <CardContent>
            {auditLog.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No verification attempts yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="hidden sm:table-cell">IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLog.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(entry.createdAt), 'MMM d HH:mm')}
                      </TableCell>
                      <TableCell className="font-mono font-bold">{entry.code}</TableCell>
                      <TableCell>
                        {entry.action === 'verify_success' ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle2 className="h-3 w-3" /> Success
                          </span>
                        ) : entry.action === 'verify_failed' ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <XCircle className="h-3 w-3" /> Failed
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">{entry.action}</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                        {entry.ipAddress || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
    </AdminLayout>
  );
}
